import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Building2, Trash2, RotateCw, PauseCircle, PlayCircle, KeyRound, Mail, Pencil, Infinity } from "lucide-react";
import { useReadOnly } from "../hooks/useReadOnly";
import { useAuth } from "../contexts/AuthContext";
import { SubscriptionNotice } from "../components/SubscriptionBanner";
import { MODULES, LEVEL_OPTIONS, defaultPermissionsForRole } from "../lib/permissions";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

const ROLE_LABEL = {
  superadmin: "Superadministrador",
  owner: "Administrador empresa",
  contador: "Contador",
  usuario: "Usuario",
  demo: "Demostración",
  admin: "Administrador",
};

const OWNER_INVITE_ROLES = [
  { value: "contador", label: "Contador" },
  { value: "usuario", label: "Usuario" },
];

const SUPER_INVITE_ROLES = [
  { value: "owner", label: "Administrador empresa" },
  { value: "contador", label: "Contador" },
  { value: "usuario", label: "Usuario" },
];

function previousMonthPeriodo() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function PermissionsGrid({ permisos, onChange, disabled }) {
  return (
    <div className="border border-border rounded-md divide-y max-h-64 overflow-y-auto">
      {MODULES.map((m) => (
        <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
          <div>
            <div className="font-medium text-foreground">{m.label}</div>
            <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wide">{m.group}</div>
          </div>
          <select
            disabled={disabled}
            value={permisos[m.id] || "none"}
            onChange={(e) => onChange({ ...permisos, [m.id]: e.target.value })}
            className="text-xs border border-border rounded-sm px-2 py-1 bg-card"
          >
            {LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

export default function Usuarios() {
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin" && !user?.organizacion_id;
  const canInvite = !readOnly && (isSuperadmin || user?.role === "owner");

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "contador",
    permisos: defaultPermissionsForRole("contador"),
    empresas: [],
  });
  const [orgEmpresas, setOrgEmpresas] = useState([]);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessForm, setAccessForm] = useState({
    role: "contador",
    permisos: defaultPermissionsForRole("contador"),
    empresas: [],
  });
  const [accessSaving, setAccessSaving] = useState(false);

  const [orgOpen, setOrgOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({
    nombre: "",
    plan: "estandar",
    max_usuarios: 25,
    max_empresas: 10,
    dias_vigencia: 365,
    empresa_nombre: "",
    empresa_rut: "",
    empresa_giro: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDias, setRenewDias] = useState(30);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editOrgForm, setEditOrgForm] = useState({
    nombre: "",
    plan: "estandar",
    max_usuarios: 25,
    max_empresas: 10,
    sin_limite: false,
    vigente_hasta: "",
    dias_adicionales: "",
    activa: true,
  });

  const [myPwd, setMyPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const [empOpen, setEmpOpen] = useState(false);
  const [empForm, setEmpForm] = useState({
    nombre: "",
    rut: "",
    giro: "",
  });
  const [orgToDelete, setOrgToDelete] = useState(null);

  const [informeOpen, setInformeOpen] = useState(false);
  const [informePeriodo, setInformePeriodo] = useState(previousMonthPeriodo());
  const [informeSending, setInformeSending] = useState(false);

  const loadOrgs = async () => {
    const { data } = await api.get("/organizaciones");
    setOrgs(data);
    if (data.length && !selectedOrg) setSelectedOrg(data[0].id);
  };

  const loadOrgEmpresas = async () => {
    try {
      if (isSuperadmin) {
        if (!selectedOrg) {
          setOrgEmpresas([]);
          return;
        }
        const { data } = await api.get(`/organizaciones/${selectedOrg}/empresas`);
        setOrgEmpresas(data || []);
      } else if (user?.organizacion_id) {
        const { data } = await api.get(`/organizaciones/${user.organizacion_id}/empresas`);
        setOrgEmpresas(data || []);
      } else {
        const { data } = await api.get("/empresas");
        setOrgEmpresas(data || []);
      }
    } catch {
      setOrgEmpresas([]);
    }
  };

  const loadUsers = async () => {
    try {
      const params = isSuperadmin && selectedOrg ? { params: { organizacion_id: selectedOrg } } : {};
      const { data } = await api.get("/usuarios", params);
      setUsers(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (isSuperadmin) {
        try {
          await loadOrgs();
        } catch (e) {
          toast.error(extractError(e));
        }
      }
      await loadOrgEmpresas();
      await loadUsers();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isSuperadmin]);

  const usersPage = useClientPagination(users, selectedOrg || "self");

  const onRoleChange = (role) => {
    setUserForm((f) => ({
      ...f,
      role,
      permisos: defaultPermissionsForRole(role),
      empresas: role === "owner" ? orgEmpresas.map((e) => e.id) : f.empresas,
    }));
  };

  const toggleEmpresa = (empresaId, formSetter) => {
    formSetter((f) => {
      const set = new Set(f.empresas || []);
      if (set.has(empresaId)) set.delete(empresaId);
      else set.add(empresaId);
      return { ...f, empresas: [...set] };
    });
  };

  const submitUser = async (e) => {
    e.preventDefault();
    const orgId = isSuperadmin ? selectedOrg : undefined;
    if (isSuperadmin && !orgId) {
      toast.error("Seleccione una organización");
      return;
    }
    if (userForm.role !== "owner" && !(userForm.empresas || []).length) {
      toast.error("Asigna al menos una empresa al usuario");
      return;
    }
    try {
      const q = new URLSearchParams({ role: userForm.role });
      if (orgId) q.set("organizacion_id", orgId);
      await api.post(`/usuarios?${q}`, {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        permisos: { modulos: userForm.permisos },
        empresas: userForm.role === "owner" ? undefined : userForm.empresas,
      });
      toast.success("Usuario creado");
      setUserOpen(false);
      setUserForm({
        name: "",
        email: "",
        password: "",
        role: "contador",
        permisos: defaultPermissionsForRole("contador"),
        empresas: [],
      });
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openAccess = (u) => {
    setAccessTarget(u);
    const mods = u.permisos?.modulos || u.permisos || defaultPermissionsForRole(u.role);
    setAccessForm({
      role: u.role === "owner" ? "owner" : (u.role || "contador"),
      permisos: typeof mods === "object" ? mods : defaultPermissionsForRole(u.role),
      empresas: [...(u.empresas || [])],
    });
    setAccessOpen(true);
  };

  const canEditAccess = (u) => {
    if (!canInvite) return false;
    if (u.role === "superadmin" || u.role === "demo") return false;
    if (user?.role === "owner" && u.role === "owner" && u.id !== user.id) return false;
    return true;
  };

  const submitAccess = async (e) => {
    e.preventDefault();
    if (!accessTarget) return;
    if (accessForm.role !== "owner" && !(accessForm.empresas || []).length) {
      toast.error("Asigna al menos una empresa");
      return;
    }
    setAccessSaving(true);
    try {
      const params = isSuperadmin && selectedOrg ? { organizacion_id: selectedOrg } : {};
      await api.put(
        `/usuarios/${accessTarget.id}/acceso`,
        {
          role: accessForm.role,
          permisos: { modulos: accessForm.permisos },
          empresas: accessForm.role === "owner" ? orgEmpresas.map((x) => x.id) : accessForm.empresas,
        },
        { params },
      );
      toast.success("Acceso actualizado");
      setAccessOpen(false);
      setAccessTarget(null);
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setAccessSaving(false);
    }
  };

  const submitMyPassword = async (e) => {
    e.preventDefault();
    if (myPwd.next !== myPwd.confirm) {
      toast.error("La nueva contraseña no coincide");
      return;
    }
    try {
      await api.put("/auth/password", {
        current_password: myPwd.current,
        new_password: myPwd.next,
      });
      toast.success("Contraseña actualizada");
      setMyPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openResetPassword = (u) => {
    setPwdTarget(u);
    setNewPwd("");
    setPwdOpen(true);
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    if (!pwdTarget) return;
    try {
      const params = isSuperadmin && selectedOrg ? { organizacion_id: selectedOrg } : {};
      await api.put(`/usuarios/${pwdTarget.id}/password`, { new_password: newPwd }, { params });
      toast.success(`Contraseña actualizada para ${pwdTarget.name}`);
      setPwdOpen(false);
      setPwdTarget(null);
      setNewPwd("");
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const canResetPassword = (u) => {
    if (!canInvite) return false;
    if (u.role === "superadmin" || u.role === "demo") return false;
    if (user?.role === "owner" && u.role === "owner" && u.id !== user.id) return false;
    return true;
  };

  const submitOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/organizaciones/provision", orgForm);
      toast.success("Organización creada con empresa y administrador");
      setOrgOpen(false);
      setOrgForm({
        nombre: "",
        plan: "estandar",
        max_usuarios: 25,
        max_empresas: 10,
        dias_vigencia: 365,
        empresa_nombre: "",
        empresa_rut: "",
        empresa_giro: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
      });
      await loadOrgs();
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const confirmDeleteOrg = async () => {
    if (!orgToDelete) return;
    try {
      await api.delete(`/organizaciones/${orgToDelete.id}`);
      toast.success(`Organización «${orgToDelete.nombre}» eliminada`);
      if (selectedOrg === orgToDelete.id) setSelectedOrg("");
      setOrgToDelete(null);
      await loadOrgs();
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const submitEmpresa = async (e) => {
    e.preventDefault();
    if (!selectedOrg) {
      toast.error("Seleccione una organización");
      return;
    }
    try {
      await api.post("/organizaciones/empresas", {
        organizacion_id: selectedOrg,
        ...empForm,
      });
      toast.success("Empresa (RUT) agregada");
      setEmpOpen(false);
      setEmpForm({ nombre: "", rut: "", giro: "" });
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const selectedOrgData = orgs.find((o) => o.id === selectedOrg);
  const selectedOrgLabel = selectedOrgData?.nombre;

  const openEditOrg = (o, e) => {
    if (e) e.stopPropagation();
    setSelectedOrg(o.id);
    const hasta = o.vigente_hasta ? String(o.vigente_hasta).slice(0, 10) : "";
    setEditOrgForm({
      nombre: o.nombre || "",
      plan: o.plan || "estandar",
      max_usuarios: o.max_usuarios ?? 25,
      max_empresas: o.max_empresas ?? 10,
      sin_limite: !!o.sin_limite,
      vigente_hasta: hasta,
      dias_adicionales: "",
      activa: o.activa !== false,
    });
    setEditOrgOpen(true);
  };

  const submitEditOrg = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      const body = {
        nombre: editOrgForm.nombre.trim(),
        plan: editOrgForm.plan,
        max_usuarios: Number(editOrgForm.max_usuarios),
        max_empresas: Number(editOrgForm.max_empresas),
        activa: editOrgForm.activa,
      };
      if (editOrgForm.sin_limite) {
        body.sin_limite = true;
      } else if (editOrgForm.dias_adicionales) {
        body.dias_adicionales = Number(editOrgForm.dias_adicionales);
      } else if (editOrgForm.vigente_hasta) {
        body.vigente_hasta = editOrgForm.vigente_hasta;
      }
      await api.patch(`/organizaciones/${selectedOrg}`, body);
      toast.success("Organización actualizada");
      setEditOrgOpen(false);
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const orgEstadoLabel = (o) => {
    if (o.sin_limite) return "Sin límite";
    if (o.expirada) return "Expirada";
    if (!o.activa) return "Suspendida";
    return "Activa";
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      const { data } = await api.post(`/organizaciones/${selectedOrg}/renovar`, {
        dias: renewDias,
      });
      toast.success(
        `Vigencia extendida hasta ${fmtDate(data.vigente_hasta)} (${data.dias_restantes ?? renewDias} días restantes)`
      );
      setRenewOpen(false);
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const submitInformeMensual = async (e) => {
    e.preventDefault();
    if (!selectedOrg) {
      toast.error("Seleccione una organización");
      return;
    }
    setInformeSending(true);
    try {
      const { data } = await api.post("/email/informes-mensuales", null, {
        params: { organizacion_id: selectedOrg, periodo: informePeriodo },
      });
      const orgName = data.organizacion_nombre || selectedOrgLabel;
      const dest = (data.destinatarios || []).join(", ");
      if (data.sent > 0) {
        toast.success(
          `Informe ${informePeriodo} enviado a ${data.sent} administrador(es) de «${orgName}»${dest ? `: ${dest}` : ""}`
        );
      } else {
        toast.warning(data.errors?.[0] || "No se envió ningún correo");
      }
      setInformeOpen(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setInformeSending(false);
    }
  };

  const toggleOrgActiva = async (org, activa) => {
    try {
      await api.patch(`/organizaciones/${org.id}/estado`, { activa });
      toast.success(activa ? "Organización reactivada" : "Organización suspendida");
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <Page>
      {!isSuperadmin && user?.organizacion && (
        <div className="mb-4 rounded-md overflow-hidden border border-border">
          <SubscriptionNotice org={user.organizacion} />
        </div>
      )}
      <PageHeader
        title={isSuperadmin ? "Organizaciones y usuarios" : "Usuarios"}
        subtitle={
          readOnly
            ? "Vista de demostración"
            : isSuperadmin
              ? "Cree clientes (organizaciones), RUT y equipo con permisos por módulo"
              : "Equipo de su organización"
        }
        testid="usuarios-header"
      />

      {isSuperadmin && (
        <section className="mb-8 bg-card border border-border rounded-md p-6" data-testid="orgs-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-lg font-medium">Organizaciones cliente</h2>
            <div className="flex gap-2">
              <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    data-testid="new-org-button"
                    className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm"
                  >
                    <Plus className="h-4 w-4" /> Nueva organización
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Alta de organización</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitOrg} className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Crea la cuenta cliente, su primer RUT y el usuario administrador (owner).
                    </p>
                    <input
                      required
                      placeholder="Nombre organización (ej. Amelu Craft)"
                      value={orgForm.nombre}
                      onChange={(e) => setOrgForm({ ...orgForm, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        min={1}
                        placeholder="Máx. usuarios"
                        value={orgForm.max_usuarios}
                        onChange={(e) => setOrgForm({ ...orgForm, max_usuarios: +e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                      />
                      <input
                        type="number"
                        min={1}
                        placeholder="Máx. empresas/RUT"
                        value={orgForm.max_empresas}
                        onChange={(e) => setOrgForm({ ...orgForm, max_empresas: +e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                      />
                    </div>
                    <input
                      type="number"
                      min={0}
                      placeholder="Días de vigencia (0 = indefinido)"
                      value={orgForm.dias_vigencia}
                      onChange={(e) => setOrgForm({ ...orgForm, dias_vigencia: +e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Usa 0 para acceso indefinido. Si indicas días, tras vencer no podrán entrar hasta renovar.
                    </p>
                    <div className="label-caps pt-2">Primera empresa (RUT)</div>
                    <input
                      required
                      placeholder="Razón social"
                      value={orgForm.empresa_nombre}
                      onChange={(e) => setOrgForm({ ...orgForm, empresa_nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <input
                      required
                      placeholder="RUT"
                      value={orgForm.empresa_rut}
                      onChange={(e) => setOrgForm({ ...orgForm, empresa_rut: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <div className="label-caps pt-2">Administrador (owner)</div>
                    <input
                      required
                      placeholder="Nombre"
                      value={orgForm.owner_name}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_name: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <input
                      required
                      type="email"
                      placeholder="Correo login"
                      value={orgForm.owner_email}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_email: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <input
                      required
                      type="password"
                      minLength={6}
                      placeholder="Contraseña"
                      value={orgForm.owner_password}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_password: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                        Crear organización
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <table className="acc-table mb-4" data-testid="orgs-table">
            <thead>
              <tr>
                <th>Organización</th>
                <th>Plan</th>
                <th>Usuarios</th>
                <th>Empresas</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th className="w-36" />
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr
                  key={o.id}
                  className={selectedOrg === o.id ? "bg-primary/5" : ""}
                  onClick={() => setSelectedOrg(o.id)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="font-medium">{o.nombre}</td>
                  <td>{o.plan}</td>
                  <td>
                    {o.n_usuarios} / {o.max_usuarios}
                  </td>
                  <td>
                    {o.n_empresas} / {o.max_empresas}
                  </td>
                  <td className="text-sm">
                    {o.sin_limite ? (
                      <span className="inline-flex items-center gap-1 text-primary">
                        <Infinity className="h-3.5 w-3.5" /> Indefinido
                      </span>
                    ) : (
                      <>
                        {fmtDate(o.vigente_hasta)}
                        {o.dias_restantes != null && !o.expirada && (
                          <span className="block text-[10px] text-muted-foreground/70">
                            {o.dias_restantes} días restantes
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        o.expirada || !o.activa
                          ? "text-destructive text-xs font-medium"
                          : "text-primary text-xs font-medium"
                      }
                    >
                      {orgEstadoLabel(o)}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title="Editar organización"
                        className="p-1.5 text-primary hover:bg-primary/10 rounded-sm"
                        onClick={(e) => openEditOrg(o, e)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Eliminar organización"
                        className="p-1.5 text-destructive hover:bg-destructive/10 rounded-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrgToDelete(o);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedOrg && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                Seleccionada: <strong className="text-foreground">{selectedOrgLabel}</strong>
                {selectedOrgData && (
                  <span className="ml-2 text-xs">
                    — {selectedOrgData.n_usuarios}/{selectedOrgData.max_usuarios} usuarios ·{" "}
                    {selectedOrgData.n_empresas}/{selectedOrgData.max_empresas} empresas
                    {selectedOrgData.sin_limite
                      ? " · vigencia indefinida"
                      : ` · vigente hasta ${fmtDate(selectedOrgData.vigente_hasta)}${selectedOrgData.expirada ? " (expirada)" : ""}`}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="inline-flex items-center gap-1 border border-primary text-primary px-2 py-1 rounded-sm hover:bg-primary/10"
                onClick={() => openEditOrg(selectedOrgData)}
              >
                <Pencil className="h-3.5 w-3.5" /> Editar límites / vigencia
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 border border-border px-2 py-1 rounded-sm hover:bg-muted"
                onClick={() => setUserOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" /> Añadir usuario
              </button>
              <Dialog open={empOpen} onOpenChange={setEmpOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border border-border px-2 py-1 rounded-sm hover:bg-muted"
                  >
                    <Building2 className="h-3.5 w-3.5" /> Añadir empresa / RUT
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Otra empresa en {selectedOrgLabel}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitEmpresa} className="space-y-3">
                    <input
                      required
                      placeholder="Razón social"
                      value={empForm.nombre}
                      onChange={(e) => setEmpForm({ ...empForm, nombre: e.target.value })}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                    />
                    <input
                      required
                      placeholder="RUT"
                      value={empForm.rut}
                      onChange={(e) => setEmpForm({ ...empForm, rut: e.target.value })}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                        Agregar
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              <Dialog
                open={informeOpen}
                onOpenChange={(open) => {
                  setInformeOpen(open);
                  if (open) setInformePeriodo(previousMonthPeriodo());
                }}
              >
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border border-border px-2 py-1 rounded-sm hover:bg-muted"
                  >
                    <Mail className="h-3.5 w-3.5" /> Enviar informe mensual
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Informe mensual — {selectedOrgLabel}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitInformeMensual} className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Se envía por correo a los administradores (owner) de esta organización, con el
                      resumen de todas sus empresas del período indicado.
                    </p>
                    <label className="block text-xs font-medium text-foreground">
                      Período (AAAA-MM)
                      <input
                        type="month"
                        required
                        value={informePeriodo}
                        onChange={(e) => setInformePeriodo(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded-sm text-sm"
                      />
                    </label>
                    <DialogFooter>
                      <button
                        type="button"
                        className="text-sm px-4 py-2 rounded-sm border border-border"
                        onClick={() => setInformeOpen(false)}
                        disabled={informeSending}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={informeSending}
                        className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm disabled:opacity-60"
                      >
                        {informeSending ? "Enviando…" : "Enviar ahora"}
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border border-border px-2 py-1 rounded-sm hover:bg-muted"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Renovar vigencia
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Renovar {selectedOrgLabel}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitRenew} className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Se suman días desde hoy o desde la fecha de expiración actual si aún no venció.
                      Para acceso indefinido usa «Editar límites / vigencia».
                    </p>
                    <input
                      type="number"
                      min={1}
                      required
                      value={renewDias}
                      onChange={(e) => setRenewDias(+e.target.value)}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                      placeholder="Días a agregar"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                        Renovar
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              {selectedOrgData && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 border border-border px-2 py-1 rounded-sm hover:bg-muted"
                  onClick={() => toggleOrgActiva(selectedOrgData, !selectedOrgData.activa)}
                >
                  {selectedOrgData.activa ? (
                    <>
                      <PauseCircle className="h-3.5 w-3.5" /> Suspender
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-3.5 w-3.5" /> Reactivar
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <Dialog open={editOrgOpen} onOpenChange={setEditOrgOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Editar organización</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitEditOrg} className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Ajusta cupos, plan y vigencia. Luego puedes añadir usuarios o empresas desde la barra de acciones.
                </p>
                <div>
                  <label className="label-caps block mb-1">Nombre</label>
                  <input
                    required
                    value={editOrgForm.nombre}
                    onChange={(e) => setEditOrgForm({ ...editOrgForm, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Plan</label>
                  <Select
                    value={editOrgForm.plan}
                    onValueChange={(v) => setEditOrgForm({ ...editOrgForm, plan: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basico">Básico</SelectItem>
                      <SelectItem value="estandar">Estándar</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label-caps block mb-1">Máx. usuarios</label>
                    <input
                      type="number"
                      min={1}
                      required
                      value={editOrgForm.max_usuarios}
                      onChange={(e) => setEditOrgForm({ ...editOrgForm, max_usuarios: +e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    {selectedOrgData && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Actuales: {selectedOrgData.n_usuarios}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Máx. empresas / RUT</label>
                    <input
                      type="number"
                      min={1}
                      required
                      value={editOrgForm.max_empresas}
                      onChange={(e) => setEditOrgForm({ ...editOrgForm, max_empresas: +e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                    />
                    {selectedOrgData && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Actuales: {selectedOrgData.n_empresas}
                      </p>
                    )}
                  </div>
                </div>

                <div className="border border-border rounded-md p-3 space-y-3">
                  <div className="label-caps">Vigencia / suscripción</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editOrgForm.sin_limite}
                      onChange={(e) => setEditOrgForm({
                        ...editOrgForm,
                        sin_limite: e.target.checked,
                        dias_adicionales: e.target.checked ? "" : editOrgForm.dias_adicionales,
                      })}
                    />
                    <Infinity className="h-3.5 w-3.5" /> Acceso indefinido (sin fecha de expiración)
                  </label>
                  {!editOrgForm.sin_limite && (
                    <>
                      <div>
                        <label className="label-caps block mb-1">Fecha de expiración</label>
                        <input
                          type="date"
                          value={editOrgForm.vigente_hasta}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, vigente_hasta: e.target.value, dias_adicionales: "" })}
                          className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label className="label-caps block mb-1">O sumar días a la vigencia</label>
                        <input
                          type="number"
                          min={1}
                          placeholder="Ej. 30, 90, 365"
                          value={editOrgForm.dias_adicionales}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, dias_adicionales: e.target.value, vigente_hasta: "" })}
                          className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                        />
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Si llenas días, se suman a la fecha actual (o a la de vencimiento si aún no expiró).
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editOrgForm.activa}
                    onChange={(e) => setEditOrgForm({ ...editOrgForm, activa: e.target.checked })}
                  />
                  Organización activa (desmarcar = suspender acceso)
                </label>

                <DialogFooter className="flex-wrap gap-2">
                  <button
                    type="button"
                    className="mr-auto text-sm border border-border px-3 py-2 rounded-sm hover:bg-muted"
                    onClick={() => { setEditOrgOpen(false); setUserOpen(true); }}
                  >
                    Añadir usuario
                  </button>
                  <button
                    type="button"
                    className="text-sm border border-border px-3 py-2 rounded-sm hover:bg-muted"
                    onClick={() => { setEditOrgOpen(false); setEmpOpen(true); }}
                  >
                    Añadir empresa
                  </button>
                  <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                    Guardar cambios
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <AlertDialog open={!!orgToDelete} onOpenChange={(open) => !open && setOrgToDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar organización?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se borrará permanentemente «{orgToDelete?.nombre}», todos sus usuarios, empresas
                  (RUT) y datos contables. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-700 hover:bg-red-800"
                  onClick={confirmDeleteOrg}
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}

      {canInvite && !readOnly && (
        <section className="bg-card border border-border rounded-md p-4 mb-4 max-w-lg">
          <h2 className="font-display text-lg font-medium mb-2 flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Mi contraseña
          </h2>
          <form onSubmit={submitMyPassword} className="space-y-2">
            <input
              type="password"
              required
              placeholder="Contraseña actual"
              value={myPwd.current}
              onChange={(e) => setMyPwd({ ...myPwd, current: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            />
            <input
              type="password"
              required
              minLength={6}
              placeholder="Nueva contraseña"
              value={myPwd.next}
              onChange={(e) => setMyPwd({ ...myPwd, next: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            />
            <input
              type="password"
              required
              minLength={6}
              placeholder="Confirmar nueva contraseña"
              value={myPwd.confirm}
              onChange={(e) => setMyPwd({ ...myPwd, confirm: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            />
            <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
              Cambiar mi contraseña
            </button>
          </form>
        </section>
      )}

      <section className="bg-card border border-border rounded-md overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
          <h2 className="font-display text-lg font-medium">
            {isSuperadmin ? `Usuarios — ${selectedOrgLabel || "seleccione org"}` : "Equipo"}
          </h2>
          {canInvite && (
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="new-user-button"
                  disabled={isSuperadmin && !selectedOrg}
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm"
                >
                  <Plus className="h-4 w-4" /> Nuevo usuario
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Alta de usuario</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitUser} className="space-y-3" data-testid="new-user-form">
                  {isSuperadmin && (
                    <p className="text-xs text-muted-foreground">
                      Organización: <strong>{selectedOrgLabel}</strong>
                    </p>
                  )}
                  <input
                    required
                    placeholder="Nombre"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                  />
                  <input
                    required
                    type="email"
                    placeholder="Correo"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                  />
                  <input
                    required
                    type="password"
                    minLength={6}
                    placeholder="Contraseña"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm text-sm"
                  />
                  <Select value={userForm.role} onValueChange={onRoleChange}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Rol" />
                    </SelectTrigger>
                    <SelectContent>
                      {(isSuperadmin ? SUPER_INVITE_ROLES : OWNER_INVITE_ROLES).map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {userForm.role !== "owner" && (
                    <div>
                      <div className="label-caps mb-2">Empresas con acceso</div>
                      {orgEmpresas.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No hay empresas en la organización.</p>
                      ) : (
                        <div className="border border-border rounded-md max-h-40 overflow-y-auto divide-y">
                          {orgEmpresas.map((emp) => (
                            <label key={emp.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                              <input
                                type="checkbox"
                                checked={(userForm.empresas || []).includes(emp.id)}
                                onChange={() => toggleEmpresa(emp.id, setUserForm)}
                              />
                              <span className="font-medium truncate">{emp.nombre}</span>
                              <span className="font-mono text-[11px] text-muted-foreground ml-auto">{emp.rut}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Solo verá y podrá trabajar en las empresas marcadas.
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="label-caps mb-2">Permisos por módulo</div>
                    <PermissionsGrid
                      permisos={userForm.permisos}
                      onChange={(permisos) => setUserForm({ ...userForm, permisos })}
                    />
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                      Crear usuario
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : (
          <>
            <table className="acc-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Empresas</th>
                  <th>Creado</th>
                  {canInvite && <th style={{ width: 160 }} />}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={canInvite ? 6 : 5} className="text-center text-muted-foreground py-8 text-sm">
                      {isSuperadmin && !selectedOrg
                        ? "Seleccione una organización"
                        : "Sin usuarios en esta organización"}
                    </td>
                  </tr>
                ) : (
                  usersPage.pageItems.map((u) => (
                    <tr key={u.id}>
                      <td className="font-medium">{u.name}</td>
                      <td className="font-mono text-[12px]">{u.email}</td>
                      <td>
                        <span className="inline-flex px-2 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-primary/10 text-primary">
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="text-[12px] text-muted-foreground">
                        {u.role === "owner"
                          ? "Todas"
                          : (u.empresas || []).length
                            ? `${(u.empresas || []).length} asignada(s)`
                            : "—"}
                      </td>
                      <td>{fmtDate(u.created_at)}</td>
                      {canInvite && (
                        <td>
                          <div className="flex flex-col gap-1 items-start">
                            {canEditAccess(u) && (
                              <button
                                type="button"
                                className="text-xs text-primary underline inline-flex items-center gap-1"
                                onClick={() => openAccess(u)}
                              >
                                <Pencil className="h-3 w-3" />
                                Acceso / rol
                              </button>
                            )}
                            {canResetPassword(u) && (
                              <button
                                type="button"
                                className="text-xs text-primary underline inline-flex items-center gap-1"
                                onClick={() => openResetPassword(u)}
                              >
                                <KeyRound className="h-3 w-3" />
                                Contraseña
                              </button>
                            )}
                            {!canEditAccess(u) && !canResetPassword(u) && "—"}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {users.length > 0 && (
              <LibroPagination
                pagination={usersPage.pagination}
                pageSize={usersPage.pageSize}
                onPageSizeChange={usersPage.setPageSize}
                onPageChange={usersPage.setPage}
                testidPrefix="users"
              />
            )}
          </>
        )}
      </section>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva contraseña — {pwdTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitResetPassword} className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Correo: <span className="font-mono">{pwdTarget?.email}</span>
            </p>
            <input
              required
              type="password"
              minLength={6}
              placeholder="Nueva contraseña"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            />
            <DialogFooter>
              <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                Guardar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={accessOpen} onOpenChange={(v) => { if (!accessSaving) setAccessOpen(v); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Acceso — {accessTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAccess} className="space-y-3">
            <p className="text-xs text-muted-foreground font-mono">{accessTarget?.email}</p>
            {accessTarget?.role !== "owner" || isSuperadmin ? (
              <Select
                value={accessForm.role}
                onValueChange={(role) =>
                  setAccessForm((f) => ({
                    ...f,
                    role,
                    permisos: defaultPermissionsForRole(role),
                    empresas: role === "owner" ? orgEmpresas.map((e) => e.id) : f.empresas,
                  }))
                }
                disabled={accessTarget?.id === user?.id}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  {(isSuperadmin ? SUPER_INVITE_ROLES : OWNER_INVITE_ROLES).map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {accessForm.role !== "owner" && (
              <div>
                <div className="label-caps mb-2">Empresas con acceso</div>
                <div className="border border-border rounded-md max-h-40 overflow-y-auto divide-y">
                  {orgEmpresas.map((emp) => (
                    <label key={emp.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={(accessForm.empresas || []).includes(emp.id)}
                        onChange={() => toggleEmpresa(emp.id, setAccessForm)}
                      />
                      <span className="font-medium truncate">{emp.nombre}</span>
                      <span className="font-mono text-[11px] text-muted-foreground ml-auto">{emp.rut}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {accessForm.role !== "owner" && (
              <div>
                <div className="label-caps mb-2">Permisos por módulo</div>
                <PermissionsGrid
                  permisos={accessForm.permisos}
                  onChange={(permisos) => setAccessForm({ ...accessForm, permisos })}
                />
              </div>
            )}
            <DialogFooter>
              <button
                type="submit"
                disabled={accessSaving}
                className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm disabled:opacity-50"
              >
                {accessSaving ? "Guardando…" : "Guardar acceso"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
